import { useEffect, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../../api/client';
import type { WaScope } from '../../api/types';
import { useWaConnect } from '../../api/whatsapp';
import type { WhatsappAccount, WhatsappConfig } from '../../api/types';
import { Alert, Button, TextField } from '../ui';

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

interface FbLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

interface FacebookSdk {
  init(options: {
    appId: string;
    autoLogAppEvents?: boolean;
    xfbml?: boolean;
    version: string;
  }): void;
  login(
    callback: (response: FbLoginResponse) => void,
    options: {
      config_id: string;
      response_type: string;
      override_default_response_type: boolean;
      extras: Record<string, unknown>;
    },
  ): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

/**
 * Meta Embedded Signup. Loads the Facebook JS SDK on demand, launches the
 * signup flow with the hub's configuration id, captures the WABA and phone
 * number ids from the SDK's message event and the one-time code from the
 * login callback, then posts them to /whatsapp/connect. A manual form is
 * offered when Embedded Signup is not configured (staff pasting a system
 * user token) or the SDK cannot load.
 */
export function WhatsAppConnectButton({
  config,
  scope = {},
  onConnected,
  allowManual = false,
}: {
  config: WhatsappConfig;
  scope?: WaScope;
  onConnected: (account: WhatsappAccount, notice: ReactNode) => void;
  allowManual?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  /** A failure before the connect call: SDK, signup canceled, ids missing. */
  const [problem, setProblem] = useState<string | null>(null);
  const [manual, setManual] = useState(!config.embeddedSignup?.configId);
  const ids = useRef<{ wabaId?: string; phoneNumberId?: string }>({});
  const connect = useWaConnect(scope);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!/facebook\.com$/.test(new URL(event.origin).hostname)) return;
      try {
        const data = JSON.parse(String(event.data)) as {
          type?: string;
          event?: string;
          data?: { phone_number_id?: string; waba_id?: string };
        };
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'FINISH' && data.data) {
          ids.current = { wabaId: data.data.waba_id, phoneNumberId: data.data.phone_number_id };
        }
      } catch {
        /* not our message */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const launch = async () => {
    const signup = config.embeddedSignup;
    if (!signup?.configId) {
      setManual(true);
      return;
    }
    setBusy(true);
    setProblem(null);
    connect.reset();
    try {
      const fb = await loadSdk(signup.appId, signup.graphVersion);
      fb.login(
        (response) => {
          const code = response.authResponse?.code;
          const { wabaId, phoneNumberId } = ids.current;
          if (!code || !wabaId || !phoneNumberId) {
            setProblem(
              code
                ? 'Meta did not report the number; finish the signup flow and try again.'
                : 'Signup was canceled before a code was issued.',
            );
            setBusy(false);
            return;
          }
          connect.mutate(
            { code, wabaId, phoneNumberId },
            {
              onSuccess: (account) =>
                onConnected(
                  account,
                  <>
                    Connected <strong>{account.displayPhoneNumber ?? account.phoneNumberId}</strong>
                    .
                  </>,
                ),
              onSettled: () => setBusy(false),
            },
          );
        },
        {
          config_id: signup.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
        },
      );
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
      setManual(true);
      setBusy(false);
    }
  };

  const error = problem ?? (connect.error ? errorMessage(connect.error) : null);
  return (
    <div className="stack">
      {error ? <Alert kind="error">{error}</Alert> : null}
      {config.embeddedSignup?.configId ? (
        <div className="form__row">
          <Button onClick={() => void launch()} loading={busy}>
            Connect a WhatsApp number
          </Button>
          {allowManual ? (
            <Button variant="ghost" className="btn--small" onClick={() => setManual((v) => !v)}>
              {manual ? 'Hide manual form' : 'Enter ids by hand'}
            </Button>
          ) : null}
        </div>
      ) : null}
      {manual && (allowManual || !config.embeddedSignup?.configId) ? (
        <ManualConnectForm scope={scope} onConnected={onConnected} />
      ) : null}
      {!config.embeddedSignup?.configId && !allowManual ? (
        <p className="muted">
          Embedded Signup is not enabled on this hub yet. Ask Balsam to connect your number.
        </p>
      ) : null}
    </div>
  );
}

/** Staff: a system user token plus the ids from Meta Business Manager. */
function ManualConnectForm({
  scope,
  onConnected,
}: {
  scope: WaScope;
  onConnected: (account: WhatsappAccount, notice: ReactNode) => void;
}) {
  const [accessToken, setAccessToken] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const connect = useWaConnect(scope);
  const submit = () =>
    connect.mutate(
      {
        accessToken: accessToken.trim(),
        wabaId: wabaId.trim(),
        phoneNumberId: phoneNumberId.trim(),
      },
      {
        onSuccess: (account) => {
          setAccessToken('');
          onConnected(
            account,
            <>
              Connected <strong>{account.displayPhoneNumber ?? account.phoneNumberId}</strong>.
            </>,
          );
        },
      },
    );
  return (
    <form
      className="form form--grid"
      aria-label="Connect by hand"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
    >
      {connect.error ? (
        <div className="form__span">
          <Alert kind="error">{errorMessage(connect.error)}</Alert>
        </div>
      ) : null}
      <TextField
        label="System user access token"
        name="accessToken"
        type="password"
        autoComplete="off"
        required
        value={accessToken}
        hint="From Meta Business Manager; stored in the secrets provider, never shown again."
        onChange={(e) => setAccessToken(e.target.value)}
      />
      <TextField
        label="WhatsApp Business Account id"
        name="wabaId"
        required
        value={wabaId}
        onChange={(e) => setWabaId(e.target.value)}
      />
      <TextField
        label="Phone number id"
        name="phoneNumberId"
        required
        value={phoneNumberId}
        onChange={(e) => setPhoneNumberId(e.target.value)}
      />
      <div className="form__span">
        <Button
          type="submit"
          loading={connect.isPending}
          disabled={!accessToken.trim() || !wabaId.trim() || !phoneNumberId.trim()}
        >
          Connect
        </Button>
      </div>
    </form>
  );
}

let sdkPromise: Promise<FacebookSdk> | null = null;

function loadSdk(appId: string, version: string): Promise<FacebookSdk> {
  if (window.FB) return Promise.resolve(window.FB);
  if (!sdkPromise) {
    sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
      window.fbAsyncInit = () => {
        if (!window.FB) return reject(new Error('Facebook SDK did not initialise'));
        window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
        resolve(window.FB);
      };
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => {
        sdkPromise = null;
        reject(new Error('Could not load the Facebook SDK (blocked by the browser?).'));
      };
      document.head.appendChild(script);
    });
  }
  return sdkPromise;
}
